import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore"; // Firestore functions for querying and listening in real-time
import { db } from "../firebase"; // Firestore database instance

/**
 * Custom hook to fetch comments for a given news article in real-time
 * @param {string} newsId - Unique ID or link of the news article
 */
const useComments = (newsId) => {
  const [comments, setComments] = useState([]); // Store array of comments for this news item
  const [error, setError] = useState(null);     // Store error message if fetching fails

  useEffect(() => {
    // Validate newsId; if invalid, reset comments and exit early
    if (!newsId || typeof newsId !== "string" || newsId.trim() === "") {
      setComments([]);
      return;
    }

    // Reference to 'comments' collection in Firestore
    const commentsRef = collection(db, "comments");

    // Build query: filter by newsId and sort descending by timestamp
    const q = query(
      commentsRef,
      where("newsId", "==", newsId),
      orderBy("timestamp", "desc") // newest comments first
    );

    let unsubscribe;

    try {
      // Listen for real-time updates using onSnapshot
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          // Map Firestore documents to plain JS objects
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,       // Firestore document ID
            ...doc.data(),    // Comment fields (text, name, timestamp, moderated)
          }));
          setComments(data); // Update state with latest comments
        },
        (err) => {
          console.error("Snapshot error:", err);
          setError("Failed to load comments."); // Update error state on snapshot failure
        }
      );
    } catch (err) {
      console.error("Unexpected Firestore error:", err);
      setError("An error occurred while connecting to comments."); // Catch unexpected errors
    }

    // Cleanup: unsubscribe from snapshot listener when component unmounts or newsId changes
    return () => {
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (unsubErr) {
          console.warn("Unsubscribe failed:", unsubErr); // Warn if unsubscribe fails
        }
      }
    };
  }, [newsId]); // Re-run effect when newsId changes

  // Return comments array and any error to the consuming component
  return { comments, error };
};

export default useComments;
